import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import Card from '../common/Card';
import { useApi } from '../../hooks/useApi';
import { getTrends } from '../../api/client';
import { BarChart3 } from 'lucide-react';

const ZONES = [
  'ALL', 'RASMUSTAV', 'Clear_Sky', 'SIDOROVICH', 'Bradislav',
  'Wirpmenistan', 'Briggosia', 'GREENMOUNTAIN',
];

const TIME_RANGES = [
  { label: '24H', value: '24h' },
  { label: '7D', value: '7d' },
  { label: '30D', value: '30d' },
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-card p-3 text-xs">
      <p className="text-text-secondary mb-1 font-mono">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }} className="font-mono">
          {entry.name}: {entry.value?.toLocaleString()}
        </p>
      ))}
    </div>
  );
}

export default function VolumeChart({ zone: propZone, className = '' }) {
  const [zone, setZone] = useState(propZone || 'ALL');
  const [range, setRange] = useState('7d');

  const { data: trends, loading } = useApi(getTrends, { zone: zone !== 'ALL' ? zone : undefined, range });

  const chartData = React.useMemo(() => {
    if (!trends) return [];
    const trendArr = Array.isArray(trends) ? trends : trends.data || trends.trends || [];
    return trendArr.map((t) => ({
      time: t.date || t.hour || t.period || '',
      buys: t.buys ?? t.buyCount ?? 0,
      sells: t.sells ?? t.sellCount ?? 0,
    }));
  }, [trends]);

  return (
    <Card title="Transaction Volume" icon={BarChart3} className={className}>
      {/* Controls */}
      <div className="flex items-center justify-between mb-4">
        {!propZone && (
          <select
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            className="bg-terminal-bg border border-terminal-border rounded px-2 py-1 text-xs text-text-secondary font-mono focus:outline-none focus:border-accent-green/50"
          >
            {ZONES.map((z) => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
        )}

        <div className="flex gap-1">
          {TIME_RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`px-2 py-1 rounded text-[10px] font-mono transition-colors ${
                range === r.value
                  ? 'bg-accent-blue/10 text-accent-blue border border-accent-blue/20'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="skeleton h-[250px] rounded" />
      ) : chartData.length === 0 ? (
        <div className="flex items-center justify-center h-[250px] text-text-muted text-sm">
          No volume data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="time"
              tick={{ fill: '#475569', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              axisLine={{ stroke: '#1e293b' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: '#475569', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              axisLine={{ stroke: '#1e293b' }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(30, 41, 59, 0.3)' }} />
            <Legend
              wrapperStyle={{ fontSize: 10, fontFamily: 'JetBrains Mono' }}
            />
            <Bar dataKey="buys" name="Buys" stackId="volume" fill="#00ff88" fillOpacity={0.8} radius={[0, 0, 0, 0]} />
            <Bar dataKey="sells" name="Sells" stackId="volume" fill="#ff3366" fillOpacity={0.8} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}
